import { useTranslation } from 'react-i18next';
import EditCustomer from './modals/EditCustomer';
import CreateCustomer from './modals/CreateCustomer';
import InviteCustomer from './modals/InviteCustomer';
import ImportCustomers from './modals/ImportCustomers';
import { AppContext } from '../../services/AppContext';
import { ModalContext } from '../../modal/ModalContext';
import MultiButtons from '../../components/global/MultiButtons';
import { React, Fragment, useState, useContext, useEffect } from 'react';
import { DeleteConfirmationModal } from '../../modal/global/DeleteConfirmationModal';

const Customers = () => {
    const { t } = useTranslation();
    const { showModal } = useContext(ModalContext);
    const { apiClient, user, notify } = useContext(AppContext);

    const [customersData, setCustomersData] = useState([]);
    const [filter, setFilter] = useState('');
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [scrollLoaded, setScrollLoaded] = useState(false);

    const take = 20;
    const apiUrl = window.env.REACT_APP_API_URL;

    const fetchData = async (filter = {}) => {
        const data = await apiClient.getUsers(
            { ...filter, role: 2, skip, take },
            false
        );

        if (data.hasErrors) return;

        setCustomersData((prev) => (skip === 0 ? data : [...prev, ...data]));
        setHasMore(data?.length === take);
    };

    const handleFilterChange = (e) => {
        setScrollLoaded(false);
        setFilter(e.target.value);
        setSkip(0);
    };

    useEffect(() => {
        if (user.role === 'Admin') {
            fetchData({ name: filter });
        }
    }, [filter, user, skip]);

    const handleAddCustomer = async () => {
        const created = await showModal(<CreateCustomer />);
        if (created) fetchData();
    };

    const handleInviteCustomer = async () => {
        await showModal(<InviteCustomer />);
    };

    const handleImportCustomers = async () => {
        const needsReload = await showModal(<ImportCustomers />);
        if (needsReload) fetchData();
    };

    const handleEditCustomer = async (customer) => {
        const updated = await showModal(<EditCustomer customerDto={customer} />);
        if (updated) fetchData();
    };

    const handleDelete = async (customer) => {
        const shouldDelete = await showModal(
            <DeleteConfirmationModal subject={customer.name} />
        );

        if (shouldDelete) {
            await apiClient.removeCustomer(customer.id);
            fetchData();
            notify(t('pages.customers.notifications.deleteSuccess'), 'success');
        }
    };

    const handleScroll = () => {
        const threshold = 0.5;
        const scrollPosition =
            document.documentElement.scrollHeight -
            (window.innerHeight + document.documentElement.scrollTop);

        if (scrollPosition > threshold || !hasMore) return;

        setScrollLoaded(true);
        setSkip((prev) => prev + take);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasMore]);

    return (
        <div>
            <div className="filter-container mg-b-1">
                <input
                    type="text"
                    placeholder={t('pages.customers.placeholders.filter')}
                    value={filter}
                    onChange={handleFilterChange}
                    className="filter-input"
                />

                <div className="mg-l-1">
                    <MultiButtons>
                        <button onClick={handleAddCustomer}>
                            {t('pages.customers.actions.add')}
                        </button>
                        <button onClick={handleInviteCustomer}>
                            {t('pages.customers.actions.invite')}
                        </button>
                        <button onClick={handleImportCustomers}>
                            {t('pages.customers.actions.import')}
                        </button>
                    </MultiButtons>
                </div>
            </div>

            <div className="grid-container-3">
                <div className="grid-header">
                    <div className="container">
                        <p>{t('pages.customers.profilePicture')}</p>
                    </div>
                </div>
                <div className="grid-header">
                    <div className="container">
                        <p>{t('pages.customers.headers.name')}</p>
                    </div>
                </div>
                <div className="grid-header">
                    <div className="container">
                    </div>
                </div>
                {customersData?.map((customer, index) => (
                    <Fragment key={index}>
                        <a href={`customer/${customer.id}`}>
                            <div className="grid-item">
                                <div className="avatar-container">
                                    {customer.profilePictureUrl && (
                                        <img
                                            src={apiUrl + customer.profilePictureUrl}
                                            alt={t('pages.customers.profilePicture')}
                                        />
                                    )}
                                </div>
                            </div>
                        </a>

                        <a href={`customer/${customer.id}`}>
                            <div className="grid-item">
                                <div className="container">{customer.name}</div>
                            </div>
                        </a>
                        <MultiButtons>
                            <button
                                className="background-warning width-100p"
                                onClick={() => handleEditCustomer(customer)}
                            >
                                {t('pages.customers.actions.edit')}
                            </button>
                            <button
                                className="background-danger width-100p"
                                onClick={() => handleDelete(customer)}
                            >
                                {t('pages.customers.actions.delete')}
                            </button>
                        </MultiButtons>
                    </Fragment>
                ))}
            </div>
        </div>
    );
};

export default Customers;